<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">通知範本管理</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="goCreate">
                <i class="ri-add-line"></i> 新增範本
              </button>
      </div>
    </div>

    <div class="page-container">
    <!-- 篩選工具欄 -->
    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filters.channel" class="form-control" @change="loadList">
          <option value="">全部管道</option>
          <option value="email">Email</option>
          <option value="sms">SMS</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filters.status" class="form-control" @change="loadList">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
        </select>
      </div>
      <div class="search-box">
        <input
          v-model="filters.name"
          type="text"
          class="form-control"
          placeholder="搜尋範本名稱"
          @keyup.enter="loadList"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group filter-group--action">
        <button class="btn btn-secondary" @click="loadList">搜尋</button>
      </div>
    </div>

    <!-- 載入中 -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <!-- 清單表格 -->
    <div v-else class="card">
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>範本名稱</th>
              <th>管道</th>
              <th>主旨（Email）</th>
              <th>狀態</th>
              <th>更新時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!items.length">
              <td colspan="6" class="empty-cell">尚無通知範本，請點擊「新增範本」建立</td>
            </tr>
            <tr v-for="item in items" :key="item.id">
              <td>{{ item.name }}</td>
              <td>
                <span :class="channelBadgeClass(item.channel)">
                  <i :class="channelIcon(item.channel)"></i> {{ channelLabel(item.channel) }}
                </span>
              </td>
              <td>{{ item.subject || '-' }}</td>
              <td>
                <span :class="item.status === 'active' ? 'badge badge-success' : 'badge badge-secondary'">
                  {{ item.status === 'active' ? '啟用' : '停用' }}
                </span>
              </td>
              <td>{{ formatDate(item.updatedAt) }}</td>
              <td>
                <button class="btn btn-sm btn-primary" @click="goEdit(item.id)"><i class="ri-edit-line"></i> 編輯</button>
                <button class="btn btn-sm btn-danger" @click="confirmDelete(item)"><i class="ri-delete-bin-line"></i> 刪除</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分頁 -->
      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.total"
        @change="changePage"
      />
    </div>

    <!-- 刪除確認 -->
    <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3>確認刪除</h3>
        </div>
        <div class="modal-body">
          確定要刪除範本「{{ deleteTarget.name }}」嗎？此操作不可復原。
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="deleteTarget = null">取消</button>
          <button class="btn btn-danger" :disabled="deleting" @click="doDelete">
            {{ deleting ? '刪除中...' : '確認刪除' }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { notificationTemplateService } from '@/api/modules/notificationTemplates.js';

const router = useRouter();

const loading = ref(false);
const items = ref([]);
const pagination = reactive({ page: 1, pageSize: 20, total: 0, totalPages: 1 });
const filters = reactive({ channel: '', status: '', name: '' });
const deleteTarget = ref(null);
const deleting = ref(false);

async function loadList() {
  loading.value = true;
  try {
    const params = {
      page: pagination.page,
      pageSize: pagination.pageSize,
      ...(filters.channel && { channel: filters.channel }),
      ...(filters.status && { status: filters.status }),
      ...(filters.name && { name: filters.name })
    };
    const res = await notificationTemplateService.list(params);
    items.value = (res.data?.items || []).filter(i => i.channel !== 'line');
    if (res.data?.pagination) {
      const p = res.data.pagination;
      Object.assign(pagination, {
        page: p.page || 1,
        pageSize: p.pageSize || 20,
        total: p.totalItems || p.total || 0,
        totalPages: p.totalPages || 1
      });
    }
  } catch (err) {
    console.error('載入通知範本失敗:', err);
  } finally {
    loading.value = false;
  }
}

function changePage(page) {
  pagination.page = page;
  loadList();
}

function goCreate() {
  router.push({ name: 'NotificationTemplateCreate' });
}

function goEdit(id) {
  router.push({ name: 'NotificationTemplateEdit', params: { id } });
}

function confirmDelete(item) {
  deleteTarget.value = item;
}

async function doDelete() {
  if (!deleteTarget.value) return;
  deleting.value = true;
  try {
    await notificationTemplateService.remove(deleteTarget.value.id);
    deleteTarget.value = null;
    await loadList();
  } catch (err) {
    console.error('刪除失敗:', err);
    alert('刪除失敗：' + (err.message || '未知錯誤'));
  } finally {
    deleting.value = false;
  }
}

function channelLabel(channel) {
  return { line: 'LINE', email: 'Email', sms: 'SMS' }[channel] || channel;
}

function channelIcon(channel) {
  return { line: 'ri-line-fill', email: 'ri-mail-line', sms: 'ri-message-2-line' }[channel] || 'ri-notification-line';
}

function channelBadgeClass(channel) {
  return {
    'badge': true,
    'badge-success': channel === 'line',
    'badge-info': channel === 'email',
    'badge-warning': channel === 'sms'
  };
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('zh-TW', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}

onMounted(loadList);
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 800px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  width: 420px;
  max-width: 90vw;
}

.modal-header h3 {
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
}

.modal-body {
  margin-bottom: 20px;
  color: #374151;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
}

.page-info {
  font-size: 14px;
  color: #6b7280;
}

.empty-cell {
  text-align: center;
  padding: 40px;
  color: #9ca3af;
}
</style>
